import { useEffect } from 'react';

import { Box, Drawer } from '@mui/material';
import { useFlag } from '@unleash/proxy-client-react';
import {
  drawerInviteAtom,
  informationInviteAtom,
  infoSessionCancelledAtom,
} from 'atoms/inviteSession';
import withAuth from 'hocs/withAuth/withAuth';
import { useAtom, useSetAtom } from 'jotai';
import { FormattedMessage } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { useCareMatrix } from 'hooks/useCareMatrix';
import useComplaintDevice from 'hooks/useComplaintDevice';
import useOnInit from 'hooks/useOnInit';
import { SpecialistRole } from 'services/specialist';

import { AgendaCalendar, AgendaTabList, PageHeader } from 'components';
import { CareMatrixModule } from 'components/CareMatrixModule';
import DrawerInvitationHistory from 'components/DrawerInvitationHistory/DrawerInvitationHistory';
import { DrawerInviteCreated } from 'components/DrawerInviteCreated/DrawerInviteCreated';
import DrawerInviteSchedule from 'components/DrawerInviteSchedule';
import { InviteDropdownButton } from 'components/InviteDropdownButton/InviteDropdownButton';

import * as S from './styles';

const CareProviderAgendaCalendar: React.FC = () => {
  const { isModuleOpen, closeModule } = useCareMatrix();
  const isInviteToggleEnabled = useFlag(FeatureToggle.Invite);
  const { handleSetComplaintDevice, isComplaintDevice } = useComplaintDevice();

  const [informationInvite, setInformationInvite] = useAtom(
    informationInviteAtom,
  );
  const setInfoSessionCancelledAtom = useSetAtom(infoSessionCancelledAtom);

  const [drawerInvite, setDrawerInvite] = useAtom(drawerInviteAtom);

  useOnInit(() => {
    if (isModuleOpen) {
      closeModule();
    }
  });

  const handleCloseDrawer = () => {
    if (informationInvite.patientName) {
      setInformationInvite({
        inviteLink: '',
        patientName: '',
        sessionCreated: '',
      });
    }
    setInfoSessionCancelledAtom(null);

    setDrawerInvite(undefined);
  };

  useEffect(() => {
    handleSetComplaintDevice(true);
  }, [handleSetComplaintDevice, isComplaintDevice]);

  return (
    <Box
      display="flex"
      alignItems="center"
      height="100vh"
      overflow="hidden"
      margin={0}
    >
      <S.Container data-testid="calendarPage">
        <PageHeader>
          <S.HeaderTitle data-testid="headerTitle">
            <FormattedMessage id="AGENDA.PAGE.TITLE" />
          </S.HeaderTitle>
        </PageHeader>

        <S.FirstSection>
          <AgendaTabList selectedIndex={1} />

          {isInviteToggleEnabled && (
            <InviteDropdownButton
              onClickCreate={() => setDrawerInvite('invite-schedule')}
              onClickManagement={() => setDrawerInvite('invitation-history')}
            />
          )}
        </S.FirstSection>

        <S.CalendarContainer>
          <AgendaCalendar />
        </S.CalendarContainer>
      </S.Container>

      {isModuleOpen && <CareMatrixModule />}

      <Drawer
        open={!!drawerInvite}
        onClose={handleCloseDrawer}
        anchor="right"
        SlideProps={{
          style: {
            background: 'white',
          },
        }}
      >
        {drawerInvite === 'invite-created' && (
          <DrawerInviteCreated
            onCloseButton={handleCloseDrawer}
            onConfirm={handleCloseDrawer}
            onSeeInvites={() => setDrawerInvite('invitation-history')}
          />
        )}

        {drawerInvite === 'invite-schedule' && (
          <DrawerInviteSchedule
            onCloseDrawer={handleCloseDrawer}
            openDrawerInviteCreated={() => setDrawerInvite('invite-created')}
          />
        )}

        {drawerInvite === 'invitation-history' && (
          <DrawerInvitationHistory
            onBackButton={() => setDrawerInvite('invite-created')}
            onCloseDrawer={handleCloseDrawer}
          />
        )}
      </Drawer>
    </Box>
  );
};

export default withAuth(CareProviderAgendaCalendar, [SpecialistRole.CP]);
